import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import {
  editProductionLine,
  fetchProductionLine,
  type ProductionLinePurpose,
  type ProductionLineView,
} from '../../merchant/api.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { useWorkspace } from '../../merchant/shell-context.js';
import {
  BackIcon,
  Button,
  Callout,
  Card,
  EmptyState,
  Eyebrow,
  PageHead,
  WarningIcon,
  useToast,
} from '../../merchant/ui/primitives.js';

const COPY = {
  en: {
    eyebrow: 'Edit project',
    title: 'Update this project',
    intro: 'Change its purpose or daily pace. Delivered content stays unchanged.',
    purposeLegend: 'What is this project for?',
    purposeHelp: 'A purpose change can refresh future recommendations.',
    otherLabel: 'In your own words',
    otherPlaceholder: 'Bring people back after Ramadan',
    cadence: 'Videos per day',
    cadenceHint: (min: number, max: number) =>
      `Your plan allows ${String(min)}–${String(max)}.`,
    decrease: 'Decrease videos per day',
    increase: 'Increase videos per day',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Project updated.',
    readOnly: 'An archived project is read-only.',
    requiredPurpose: 'Choose one purpose to continue.',
    requiredOther: 'Add a short description for this purpose.',
    failed: 'The project could not be saved. Refresh it and try again.',
    unavailable: 'This project can’t be edited right now.',
  },
  ar: {
    eyebrow: 'تعديل المشروع',
    title: 'حدّث المشروع',
    intro: 'غيّر هدفه أو عدد الفيديوهات باليوم. والمحتوى اللي انسلّم يبقى زي ما هو.',
    purposeLegend: 'وش هدف المشروع هذا؟',
    purposeHelp: 'إذا غيّرت الهدف، يمكن تتحدث التوصيات الجاية.',
    otherLabel: 'اكتب الهدف بكلماتك',
    otherPlaceholder: 'أرجّع العملاء بعد رمضان',
    cadence: 'عدد الفيديوهات باليوم',
    cadenceHint: (min: number, max: number) =>
      `خطتك تسمح بـ ${String(min)}–${String(max)}.`,
    decrease: 'تقليل العدد',
    increase: 'زيادة العدد',
    save: 'احفظ التغييرات',
    saving: 'جاري الحفظ…',
    saved: 'انحدّث المشروع.',
    readOnly: 'المشروع المؤرشف للقراءة بس.',
    requiredPurpose: 'اختر هدف واحد عشان تكمل.',
    requiredOther: 'ضيف وصف مختصر لهذا الهدف.',
    failed: 'ما قدرنا نحفظ المشروع. حدّث الصفحة وجرّب مرة ثانية.',
    unavailable: 'ما تقدر تعدّل المشروع الحين.',
  },
} as const;

/**
 * The configured line's editor — purpose and daily pace — on the server's
 * own options and entitlement, in the workspace's geometry.
 */
export function ProductionLineEditPage(): ReactNode {
  const { language } = useLanguage();
  const copy = COPY[language];
  const ws = workspaceCopy(language);
  const navigate = useNavigate();
  const toast = useToast();
  const workspace = useWorkspace();
  const { productionLineId = '' } = useParams<{ productionLineId: string }>();
  const titleId = useId();
  const [line, setLine] = useState<ProductionLineView | null>(null);
  const [purpose, setPurpose] = useState<ProductionLinePurpose | null>(null);
  const [otherPurpose, setOtherPurpose] = useState('');
  const [videosPerDay, setVideosPerDay] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const intentKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (productionLineId.length === 0) return;
    setFailed(false);
    try {
      const result = await fetchProductionLine(productionLineId);
      if ('pending' in result) throw new Error('pending');
      setLine(result);
      setPurpose(result.productionLine.purpose.type);
      setOtherPurpose(result.productionLine.purpose.note ?? '');
      setVideosPerDay(result.productionLine.videosPerDay);
    } catch {
      setFailed(true);
    }
  }, [productionLineId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (line === null || videosPerDay === null) return;
    if (purpose === null) {
      setError(copy.requiredPurpose);
      return;
    }
    const note = otherPurpose.trim();
    if (purpose === 'OTHER' && note.length === 0) {
      setError(copy.requiredOther);
      return;
    }
    setError(null);
    setSaving(true);
    intentKey.current ??= newIdempotencyKey();
    try {
      await editProductionLine(
        productionLineId,
        {
          expectedConfigurationId: line.productionLine.configuration.configurationId,
          purpose: { type: purpose, note: purpose === 'OTHER' ? note : null },
          videosPerDay,
        },
        intentKey.current,
      );
      intentKey.current = null;
      toast(copy.saved);
      void workspace.refresh();
      void navigate(`/production-lines/${encodeURIComponent(productionLineId)}`, {
        replace: true,
      });
    } catch {
      setError(copy.failed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mj-ws-page is-narrow" aria-labelledby={titleId}>
      <nav aria-label={ws.line.breadcrumb} className="mj-ws-crumb mj-ws-muted mj-ws-sm">
        <Link to={`/production-lines/${encodeURIComponent(productionLineId)}`}>
          <BackIcon />
          {line?.productionLine.title ?? ws.lines.title}
        </Link>
      </nav>
      <div className="mj-ws-detail-head">
        <PageHead
          eyebrow={copy.eyebrow}
          title={copy.title}
          titleId={titleId}
          intro={copy.intro}
        />
      </div>
      {failed ? (
        <EmptyState
          className="mj-ws-mt-24"
          body={copy.unavailable}
          action={
            <Button small onClick={() => void load()}>
              {ws.common.tryAgain}
            </Button>
          }
        />
      ) : line === null || purpose === null || videosPerDay === null ? (
        <p className="mj-ws-loading" role="status">
          {ws.common.loading}
        </p>
      ) : !line.editor.available ? (
        <Callout className="mj-ws-mt-24">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">{copy.readOnly}</span>
        </Callout>
      ) : (
        <Card padding={24} className="mj-ws-col mj-ws-mt-24">
          <fieldset>
            <legend className="mj-ws-legend">{copy.purposeLegend}</legend>
            <p className="mj-ws-muted mj-ws-sm">{copy.purposeHelp}</p>
            {line.editor.purposeOptions.map((option) => (
              <label key={option.id} className="mj-ws-radio">
                <input
                  type="radio"
                  name="production-purpose"
                  value={option.id}
                  checked={purpose === option.id}
                  disabled={option.state === 'disabled'}
                  onChange={() => {
                    setPurpose(option.id);
                    if (option.id !== 'OTHER') setOtherPurpose('');
                    setError(null);
                    intentKey.current = null;
                  }}
                />
                <span>{option.label[language]}</span>
              </label>
            ))}
          </fieldset>
          {purpose === 'OTHER' ? (
            <div className="mj-ws-field">
              <label className="mj-ws-lab" htmlFor={`${titleId}-other`}>
                {copy.otherLabel}
              </label>
              <textarea
                id={`${titleId}-other`}
                className="mj-ws-inp"
                rows={2}
                maxLength={line.editor.otherMaxLength}
                value={otherPurpose}
                placeholder={copy.otherPlaceholder}
                onChange={(event) => {
                  setOtherPurpose(event.target.value);
                  setError(null);
                  intentKey.current = null;
                }}
              />
              <span className="mj-ws-muted mj-ws-xs">
                {otherPurpose.length}/{line.editor.otherMaxLength}
              </span>
            </div>
          ) : null}
          <div>
            <Eyebrow>{copy.cadence}</Eyebrow>
            <p className="mj-ws-muted mj-ws-sm">
              {copy.cadenceHint(
                line.editor.entitlement.min,
                line.editor.entitlement.max,
              )}
            </p>
            <div
              className="mj-ws-row mj-ws-mt-8"
              role="group"
              aria-label={copy.cadence}
            >
              <Button
                small
                aria-label={copy.decrease}
                disabled={videosPerDay <= line.editor.entitlement.min}
                onClick={() => {
                  setVideosPerDay(
                    Math.max(line.editor.entitlement.min, videosPerDay - 1),
                  );
                  intentKey.current = null;
                }}
              >
                −
              </Button>
              <output
                className="mj-ws-tnum"
                aria-live="polite"
                style={{ minWidth: 32, textAlign: 'center' }}
              >
                {videosPerDay}
              </output>
              <Button
                small
                aria-label={copy.increase}
                disabled={videosPerDay >= line.editor.entitlement.max}
                onClick={() => {
                  setVideosPerDay(
                    Math.min(line.editor.entitlement.max, videosPerDay + 1),
                  );
                  intentKey.current = null;
                }}
              >
                +
              </Button>
            </div>
          </div>
          {error !== null ? (
            <div className="mj-ws-err" role="alert">
              {error}
            </div>
          ) : null}
          <div>
            <Button variant="primary" disabled={saving} onClick={() => void submit()}>
              {saving ? copy.saving : copy.save}
            </Button>
          </div>
        </Card>
      )}
    </section>
  );
}
